import React, { FC } from "react";
import { useNavigate } from "react-router";
import { BadgeDefinition, UserBadgesResponse } from "@shared/models";
import { NameOfChildRoutes } from "@shared/constants";

// CardHead is relative, not aliased: jest resolves no path aliases here, so an aliased import would
// make every suite that mounts this section virtual-mock it just to render.
import { CardHead } from "../../CardHead";
// The builder and the tile live on the shared `components/Badges/` shelf (the dedicated Badges screen
// draws the same two), reached by direct relative path for the same jest reason as `CardHead` above.
import { buildBadgeCards, splitBadgeCards, BadgeCardViewModel } from "../../Badges/buildBadgeCards";
import BadgeCard from "../../Badges/BadgeCard/BadgeCard";
import "./index.scss";

interface BadgesSectionProps {
  badges: UserBadgesResponse;
  definitions: BadgeDefinition[];
  onBadgeClick: (badge: BadgeCardViewModel) => void;
  // Carried into the See-all link so the Received/Sent tab survives the round trip to the dedicated
  // Badges screen and back -- `RecognitionListHeader`'s own `activeTab` prop, verbatim, for the
  // identical reason (without it, going back lands a viewer who came from Sent on Received).
  activeTab?: number;
}

/**
 * The badges block on My Sparks (overview-rebuild-pr3-plan.md): one headed, flat grid replacing the
 * old five stacked `BadgeCategoryGroup` sections, each with its own heading and its own independent
 * "Show N more". `badges`/`definitions` are joined and ordered once by `buildBadgeCards` (earned
 * first, most-recent first, then locked with next-tier cards ahead of the rest — see its own JSDoc),
 * then cut to this block's preview slice by `splitBadgeCards`, which now takes no expanded/collapsed
 * argument at all: this block is a fixed preview, not an in-place expander (see below).
 *
 * all-badges-screen-plan.md: "See all" now NAVIGATES to the dedicated Badges screen
 * (`NameOfChildRoutes.RECOGNITIONS.BADGES`) instead of toggling this block open in place — mirrors
 * `RecognitionListHeader`'s identical "Recent recognitions" -> `AllRecognitionsContainer` link exactly,
 * down to carrying `?activeTab=` through, and including the unconditional slot (no "is anything
 * actually hidden" guard: `RecognitionListHeader`'s own "See all" shows even when its preview already
 * contains every row). The `expanded` local state and the
 * "Show less" label both come out with it — the dedicated screen is where the rest of the catalog and
 * the per-category "N of M" counts live now (`buildCategorySummaries`), not an expanded version of this
 * block.
 *
 * Otherwise unchanged: presentational, no dispatch/select of its own beyond the router's `useNavigate`,
 * `onBadgeClick` threaded straight through to every card, `MySparksContainer` owns the actual
 * `showOverlay` dispatch. Renders `null` for a genuinely empty catalog (no head, no grid) — the same
 * guard this component has always had.
 */
const BadgesSection: FC<BadgesSectionProps> = (props) => {
  const { badges, definitions, onBadgeClick, activeTab } = props;
  const navigate = useNavigate();

  const cards = buildBadgeCards(definitions, badges);

  if (cards.length === 0) {
    return null;
  }

  const { visibleCards } = splitBadgeCards(cards);

  const goToAllBadges = () => {
    navigate(
      `${NameOfChildRoutes.RECOGNITIONS.ROOT}${NameOfChildRoutes.RECOGNITIONS.BADGES}?activeTab=${activeTab ?? 0}`,
    );
  };

  return (
    <div className="badges-section">
      {/* The shared SPARK/CardHead in its `link` variant. The frame (9401:2787) puts "See all" and
          its arrow in the head's right slot, where every other block on this screen puts its own, and
          the head carries no count: the slot holds one thing, and the design spends it on the action.
          The earned/total count that used to sit here is on the dedicated Badges screen's own category
          heads instead ("10 of 40", "2 of 5"), which is where the component description's
          "12 of 84 earned" caption example comes from. */}
      <CardHead
        title="Badges"
        slot={{ kind: "link", label: "See all", onClick: goToAllBadges }}
        className="badges-section-head"
      />
      <div className="badges-section-grid">
        {visibleCards.map((card) => (
          <BadgeCard key={card.definitionId} badge={card} onBadgeClick={onBadgeClick} />
        ))}
      </div>
    </div>
  );
};

export default BadgesSection;
